import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchAccountingTransaction,
  useAccountingDispatch,
} from "../Context";

/**
 * useInvoiceLink - lets another page link into an invoice or bill with a plain
 * <a href>: `?invoiceId=<id>` opens that record's detail, `?new=invoice` the
 * new-invoice form.
 *
 * The parameter is dropped from the URL as soon as it is read, so closing the
 * modal or refreshing does not bring it back. A record that cannot be read
 * leaves the page as it is: the list underneath is still where the user was
 * sent.
 *
 * The record just read is newer than the page list's copy of it, which the
 * detail drawer prefers, so it replaces that copy too.
 */
export const useInvoiceLink = ({ onOpen, onCreate }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountingDispatch = useAccountingDispatch();
  const invoiceId = searchParams.get("invoiceId");
  const create = searchParams.get("new") === "invoice";

  // Held in refs so the effect only reacts to the URL, not to the caller
  // passing fresh callbacks on every render.
  const handlers = useRef({ onOpen, onCreate });
  handlers.current = { onOpen, onCreate };

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!invoiceId && !create) return;

    const next = new URLSearchParams(searchParams);
    next.delete("invoiceId");
    next.delete("new");
    setSearchParams(next, { replace: true });

    if (create) {
      handlers.current.onCreate?.();
      return;
    }

    fetchAccountingTransaction(invoiceId)
      .then((invoice) => {
        if (!mounted.current || !invoice) return;
        accountingDispatch({
          type: "REFRESH_ACCT_TRANSACTION_SUCCESS",
          payload: { transaction: invoice },
        });
        handlers.current.onOpen?.(invoice);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, create]);
};
